import { useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import localeText from "../../../../locale";
import OrderService from "../../../../service/OrderService";
import useAdminList, { expectCode } from "../../hooks/useAdminList";
import useAdminAction from "../../hooks/useAdminAction";
import { buildSearchText, formatDay, getAdminUsername, normalizeText } from "../../utils/adminUtils";
import SearchInput from "../../components/list/SearchInput";
import EmptyState from "../../components/list/EmptyState";
import ConfirmActionDialog from "../../components/list/ConfirmActionDialog";

// A API devolve apenas as transferências de cadastro ainda não confirmadas
const loadPendingTransfers = async () => {
  const res = await new OrderService().getPendingTransfers();
  return expectCode(res, 200001).result;
};

const AdminBankTransfers = () => {
  const { items, loading, loadError, reload } = useAdminList(loadPendingTransfers);
  const { run, runningId } = useAdminAction();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const rows = useMemo(
    () =>
      items.map((row) => ({
        ...row,
        username: row.user?.username,
        planDescription: row.plan?.description,
        searchText: buildSearchText([row.user?.username, row.plan?.description]),
      })),
    [items],
  );

  const filteredRows = useMemo(() => {
    const term = normalizeText(search.trim());
    return term ? rows.filter((row) => row.searchText.includes(term)) : rows;
  }, [rows, search]);

  const authorizeSelected = async () => {
    const ok = await run(selected.id, async () =>
      expectCode(
        await new OrderService().confirmTransferRegisterUser({
          idorder: selected.id,
          username: getAdminUsername(),
        }),
        200000,
      ),
      "adminBankTransferAuthorized",
    );
    setSelected(null);
    if (ok) reload();
  };

  const hasFilters = !!search.trim();

  return (
    <div className="admin-list">
      <ConfirmActionDialog
        visible={!!selected}
        message={localeText("adminBankTransferConfirmMessage")}
        details={
          selected
            ? [
                { label: localeText("username"), value: selected.username && `@${selected.username}` },
                { label: localeText("plan"), value: selected.planDescription },
                { label: localeText("requestDate"), value: formatDay(selected.date) },
              ]
            : []
        }
        confirmLabel={localeText("authorize")}
        loading={!!selected && runningId === selected.id}
        onConfirm={authorizeSelected}
        onCancel={() => setSelected(null)}
      />

      <section className="admin-list__card">
        <div className="admin-list__toolbar">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={localeText("adminBankTransferSearch")}
          />
          <span className="admin-list__result-count">
            {loading ? "" : `${filteredRows.length} ${localeText("adminBankTransferPending")}`}
          </span>
        </div>

        <div className="admin-list__table">
          <DataTable
            value={filteredRows}
            dataKey="id"
            loading={loading}
            paginator
            rows={10}
            rowsPerPageOptions={[10, 20, 50]}
            paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate={localeText("showingFirstToLast")}
            sortField="date"
            sortOrder={1}
            removableSort
            scrollable
            tableStyle={{ minWidth: "48rem" }}
            emptyMessage={
              loadError ? (
                <EmptyState
                  icon="pi pi-exclamation-triangle"
                  message={localeText("adminListLoadError")}
                  actionLabel={localeText("adminUsersRetry")}
                  onAction={reload}
                />
              ) : (
                <EmptyState
                  icon={hasFilters ? "pi pi-inbox" : "pi pi-check-circle"}
                  message={localeText(hasFilters ? "adminListNoResults" : "adminBankTransferNone")}
                  actionLabel={hasFilters ? localeText("adminUsersClearFilters") : null}
                  onAction={() => setSearch("")}
                />
              )
            }
          >
            <Column
              header={localeText("adminUsersUser")}
              field="username"
              body={(row) => (
                <span className="admin-strong">{row.username ? `@${row.username}` : "—"}</span>
              )}
              sortable
            />
            <Column header={localeText("plan")} field="planDescription" sortable />
            <Column
              header={localeText("requestDate")}
              field="date"
              body={(row) => formatDay(row.date)}
              sortable
            />
            <Column
              header={localeText("receipt")}
              body={(row) =>
                row.transferbankReceipt ? (
                  <a
                    className="admin-btn"
                    href={row.transferbankReceipt}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="pi pi-external-link" aria-hidden="true" />
                    {localeText("receipt")}
                  </a>
                ) : (
                  <span className="admin-muted">—</span>
                )
              }
            />
            <Column
              header={localeText("actions")}
              frozen
              alignFrozen="right"
              headerClassName="admin-list__actions-cell"
              bodyClassName="admin-list__actions-cell"
              body={(row) => (
                <button
                  type="button"
                  className="admin-btn admin-btn--primary"
                  onClick={() => setSelected(row)}
                  disabled={runningId === row.id}
                >
                  <i
                    className={runningId === row.id ? "pi pi-spin pi-spinner" : "pi pi-check"}
                    aria-hidden="true"
                  />
                  {localeText("authorize")}
                </button>
              )}
            />
          </DataTable>
        </div>
      </section>
    </div>
  );
};

export default AdminBankTransfers;
